import * as React from "react";

import { toast } from "@replohq/lattice/components/toast/Toast";
import { sizeValidator } from "@v2/features/assets/utils/sizeValidator";
import { useCurrentChat } from "@v2/features/chat/hooks/useCurrentChat";
import { createChatFileAttachments } from "@v2/features/chat/utils/chatFileAttachment";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";

const MAX_FILE_ATTACHMENTS = 20;

type MessagePopoverType = "inspiration" | "contextMenu" | null;

const CurrentChatContext = React.createContext<
  | (ReturnType<typeof useCurrentChat> & {
      messagePopoverType: MessagePopoverType;
      dispatchCurrentChat: (action: CurrentChatAction) => void;
      isContextMenuOpen: boolean;
      addFiles: (files: File[] | null) => Promise<void>;
    })
  | undefined
>(undefined);

type CurrentChatAction =
  | {
      type: "openContextMenu";
    }
  | {
      type: "closeContextMenu";
    }
  | {
      type: "closeInspirationMenu";
    }
  | {
      type: "openInspirationMenu";
    };

export const CurrentChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const result = useCurrentChat();

  const [messagePopoverType, setMessagePopoverType] = React.useState<
    "inspiration" | "contextMenu" | null
  >(null);

  const addFiles = React.useCallback(
    async (newFiles: File[] | null) => {
      if (!newFiles || newFiles.length === 0) {
        return;
      }

      // NOTE (Berkley, 2026-04-28, USE-2464): Reject oversized files before they
      // enter the chat. Without this, large attachments (e.g. multi-MB animated
      // GIFs) get persisted into the chat history, exceed Anthropic's per-image
      // limit, and permanently break every subsequent turn in the chat because
      // the bad image URL is replayed to the LLM each time.
      const acceptedFiles: File[] = [];
      const rejectedFiles: { file: File; message: string }[] = [];
      for (const file of newFiles) {
        const sizeError = sizeValidator(file);
        if (sizeError) {
          rejectedFiles.push({ file, message: sizeError.message });
        } else {
          acceptedFiles.push(file);
        }
      }

      if (rejectedFiles.length > 0) {
        const firstRejection = rejectedFiles[0]!;
        toast.error({
          title:
            rejectedFiles.length === 1
              ? `${firstRejection.file.name} is too large`
              : `${rejectedFiles.length} files are too large`,
          description: firstRejection.message,
        });
      }

      if (acceptedFiles.length === 0) {
        return;
      }

      const attachments = await createChatFileAttachments(acceptedFiles);

      result.setFiles((currentFiles) => {
        const currentCount = currentFiles?.length ?? 0;
        const newCount = attachments.length;

        if (currentCount + newCount > MAX_FILE_ATTACHMENTS) {
          toast.error({
            title: "Too many attachments",
            description: `You can add at most ${MAX_FILE_ATTACHMENTS} attachments to a message.`,
          });
          return currentFiles;
        }

        if (currentFiles === null) {
          return attachments;
        }
        return [...currentFiles, ...attachments];
      });
    },
    [result.setFiles],
  );

  const dispatchCurrentChat = (action: CurrentChatAction) => {
    return exhaustiveSwitch(
      action,
      "type",
    )({
      openContextMenu: () => {
        setMessagePopoverType("contextMenu");
      },
      closeContextMenu: () => {
        if (messagePopoverType === "contextMenu") {
          setMessagePopoverType(null);
        }
      },
      closeInspirationMenu: () => {
        if (messagePopoverType === "inspiration") {
          setMessagePopoverType(null);
        }
      },
      openInspirationMenu: () => {
        setMessagePopoverType("inspiration");
      },
    });
  };

  return (
    <CurrentChatContext.Provider
      value={{
        ...result,
        messagePopoverType,
        dispatchCurrentChat,
        isContextMenuOpen: messagePopoverType === "contextMenu",
        addFiles,
      }}
    >
      {children}
    </CurrentChatContext.Provider>
  );
};

export const useCurrentChatContext = () => {
  return useRequiredContext(CurrentChatContext);
};
